import { FACE, PORTRAIT } from "@/lib/avatar/geometry";

const VERTEX = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = vec2((aPos.x + 1.0) * 0.5, (1.0 - aPos.y) * 0.5);
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// Warps the original photo only: lids slide over the eyes, eye content shifts for gaze, the lower lip and chin move
// down to open the mouth, and the whole portrait moves a little for head motion. No pixels are generated.
const FRAGMENT = `
precision highp float;
varying vec2 vUv;
uniform sampler2D uTex;
uniform vec2 uSize;
uniform float uRoll;
uniform vec3 uEyeA; uniform vec4 uEyeALids;
uniform vec3 uEyeB; uniform vec4 uEyeBLids;
uniform vec4 uMouth; uniform vec3 uLowerLip;
uniform vec2 uPivot;
uniform float uBlink; uniform float uOpen; uniform vec2 uGaze;
uniform vec4 uHead;

vec2 toLocal(vec2 p, vec2 c) { vec2 d = p - c; float s = sin(-uRoll), k = cos(-uRoll); return vec2(k * d.x - s * d.y, s * d.x + k * d.y); }
vec2 toWorld(vec2 l, vec2 c) { float s = sin(uRoll), k = cos(uRoll); return c + vec2(k * l.x - s * l.y, s * l.x + k * l.y); }
float lid(float x, float hw, vec2 apex) { float u = (x - apex.x * (1.0 - (x / hw) * (x / hw))) / hw; return apex.y * clamp(1.0 - u * u, 0.0, 1.0); }

vec2 eye(vec2 q, vec3 E, vec4 L) {
  vec2 l = toLocal(q, E.xy);
  float hw = E.z;
  if (abs(l.x) >= hw) return q;
  float top = lid(l.x, hw, L.xy);
  float bot = lid(l.x, hw, L.zw);
  float oh = bot - top;
  if (oh <= 1.0) return q;
  float a = top - 40.0;
  float e = top + uBlink * oh;
  if (l.y >= a && l.y < e) {
    l.y = a + (l.y - a) * (top - a) / max(e - a, 0.001);
    return toWorld(l, E.xy);
  }
  if (l.y > top && l.y < bot) {
    float g = clamp(1.0 - pow(l.x / (hw * 0.78), 2.0), 0.0, 1.0);
    l -= uGaze * vec2(3.2, 2.0) * g;
    return toWorld(l, E.xy);
  }
  return q;
}

void main() {
  vec2 p = vUv * uSize;
  float s = sin(-uHead.z), k = cos(-uHead.z);
  vec2 d = (p - uPivot - uHead.xy) / uHead.w;
  vec2 q = uPivot + vec2(k * d.x - s * d.y, s * d.x + k * d.y);
  float shade = 1.0;
  vec2 m = toLocal(q, uMouth.xy);
  float yl = uLowerLip.x * m.x * m.x + uLowerLip.y * m.x + uLowerLip.z;
  float hm = pow(clamp(1.0 - pow(m.x / (uMouth.z * 0.92), 2.0), 0.0, 1.0), 1.5);
  float jaw = 14.0 * uOpen * hm;
  if (m.y >= yl && jaw > 0.01) {
    if (m.y < yl + jaw) {
      float t = (m.y - yl) / jaw;
      m.y = yl - 0.5;
      shade = 1.0 - 0.6 * sin(t * 3.14159) * clamp(uOpen * 1.5, 0.0, 1.0);
    } else {
      m.y -= jaw * (1.0 - smoothstep(uMouth.w * 0.95, uMouth.w * 1.7, m.y));
    }
    q = toWorld(m, uMouth.xy);
  }
  q = eye(q, uEyeA, uEyeALids);
  q = eye(q, uEyeB, uEyeBLids);
  vec3 col = texture2D(uTex, clamp(q / uSize, 0.0, 1.0)).rgb * shade;
  gl_FragColor = vec4(col, 1.0);
}`;

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh) || "shader");
  return sh;
}

export class AvatarRenderer {
  constructor(canvas, image) {
    const gl = canvas.getContext("webgl", { alpha: false, antialias: false, premultipliedAlpha: false, preserveDrawingBuffer: true })
      || canvas.getContext("experimental-webgl");
    if (!gl) throw new Error("webgl unavailable");
    this.gl = gl;
    this.canvas = canvas;
    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERTEX));
    gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error("link");
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "aPos");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, image);
    const u = (name) => gl.getUniformLocation(prog, name);
    this.u = { blink: u("uBlink"), open: u("uOpen"), gaze: u("uGaze"), head: u("uHead") };
    // Geometry is defined on the 832 px master; the shader works in master pixels whatever texture size is loaded.
    gl.uniform2f(u("uSize"), PORTRAIT.width, PORTRAIT.height);
    gl.uniform1f(u("uRoll"), FACE.roll);
    gl.uniform3fv(u("uEyeA"), FACE.eyeA);
    gl.uniform4fv(u("uEyeALids"), FACE.eyeALids);
    gl.uniform3fv(u("uEyeB"), FACE.eyeB);
    gl.uniform4fv(u("uEyeBLids"), FACE.eyeBLids);
    gl.uniform4fv(u("uMouth"), FACE.mouth);
    gl.uniform3fv(u("uLowerLip"), FACE.lowerLip);
    gl.uniform2fv(u("uPivot"), FACE.pivot);
  }

  resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.round(this.canvas.clientWidth * dpr);
    const h = Math.round(this.canvas.clientHeight * dpr);
    if (w && h && (this.canvas.width !== w || this.canvas.height !== h)) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  draw({ blink, open, gaze, head }) {
    const { gl, u } = this;
    gl.uniform1f(u.blink, blink);
    gl.uniform1f(u.open, open);
    gl.uniform2f(u.gaze, gaze[0], gaze[1]);
    gl.uniform4f(u.head, head[0], head[1], head[2], head[3]);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}
